import { Helmet } from 'react-helmet-async';
import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { getFirestore, collection, query, where, getDocs, getDoc, setDoc, doc, orderBy } from 'firebase/firestore';
import Iconify from '../components/iconify';
import firebaseApp from '../utils/firebaseConfig';
import { hasPermission, PERMISSIONS } from '../protectedRoute';
import { isPrivateCourse } from '../utils/privateCourse';
import {
  INSTRUCTOR_PAY_SETTINGS_DOC,
  PRIVATE_RATE_KEYS,
  money,
  toCents,
  normalizeSettings,
  buildSessions,
  instructorReport,
  openInstructorReports,
  downloadInstructorReportsPdf,
  periodLabel,
} from '../utils/instructorPay';

const db = getFirestore(firebaseApp);

const isoOf = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const formatTime12 = (t) => {
  if (!t) return '';
  const [h, m] = t.split(':');
  const hour = parseInt(h, 10);
  return `${hour % 12 || 12}:${m} ${hour >= 12 ? 'PM' : 'AM'}`;
};

const dollars = (cents) => (cents == null ? '' : (Number(cents) / 100).toFixed(2));

// Pago a instructores: cuántos alumnos atendió cada instructor en el periodo,
// tarifa por alumno según el curso, retención de servicios profesionales y
// neto a pagar. Los primeros $500 del año no llevan retención.
export default function InstructorPayPage() {
  const canEdit = hasPermission(PERMISSIONS.EDIT_PAYMENTS);
  const today = new Date();
  const weekAgo = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 6);
  const [startDate, setStartDate] = useState(isoOf(weekAgo));
  const [endDate, setEndDate] = useState(isoOf(today));
  const [filterInstructor, setFilterInstructor] = useState('');

  const [instructors, setInstructors] = useState([]);
  const [categories, setCategories] = useState([]);
  const [settings, setSettings] = useState(normalizeSettings(null));
  const [loadingBase, setLoadingBase] = useState(true);

  // Datos del reporte: sesiones desde el 1 de enero hasta la fecha final
  const [sessions, setSessions] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Tarifas (settings/instructorPay)
  const [ratesOpen, setRatesOpen] = useState(false);
  const [ratesForm, setRatesForm] = useState(null);
  const [ratesSaving, setRatesSaving] = useState(false);
  const [ratesMsg, setRatesMsg] = useState('');

  // Asignar instructores a una sesión
  const [assignSession, setAssignSession] = useState(null);
  const [assignChecked, setAssignChecked] = useState({});
  const [assignSaving, setAssignSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [empSnap, catSnap, setSnap] = await Promise.all([
          getDocs(collection(db, 'employees')),
          getDocs(query(collection(db, 'reservation_categories'), where('type', '==', 'course'))),
          getDoc(doc(db, 'settings', INSTRUCTOR_PAY_SETTINGS_DOC)),
        ]);
        // Igual que en Cursos: solo los empleados con rol "Instructor"
        const list = empSnap.docs.map((d) => ({ id: d.id, ...d.data() }))
          .filter((e) => (e.name || '').trim())
          .filter((e) => Array.isArray(e.roles) && e.roles.some((rl) => /instructor/i.test(rl)))
          .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'es'));
        setInstructors(list);
        setCategories(catSnap.docs.map((d) => ({ id: d.id, ...d.data() })).sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'es')));
        setSettings(normalizeSettings(setSnap.exists() ? setSnap.data() : null));
      } catch (e) { console.error(e); }
      setLoadingBase(false);
    })();
  }, []);

  const yearStart = `${startDate.slice(0, 4)}-01-01`;

  const handleGenerate = async () => {
    if (!startDate || !endDate || endDate < startDate) {
      setError('Escoge un rango de fechas válido.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      // Se cargan todos los cursos del año hasta la fecha final para saber
      // cuánto lleva cobrado cada instructor (los $500 exentos).
      const [resSnap, metaSnap] = await Promise.all([
        getDocs(query(collection(db, 'reservations'), where('date', '>=', yearStart), where('date', '<=', endDate), orderBy('date'))),
        getDocs(query(collection(db, 'course_session_meta'), where('date', '>=', yearStart), where('date', '<=', endDate))),
      ]);
      const reservations = resSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const metas = metaSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setSessions(buildSessions(reservations, metas));
    } catch (e) {
      console.error(e);
      setError('Error generando el reporte. Intenta de nuevo.');
    }
    setLoading(false);
  };

  const reports = useMemo(() => {
    if (!sessions) return null;
    return instructors
      .filter((i) => !filterInstructor || i.id === filterInstructor)
      .map((i) => instructorReport(i, sessions, { startDate, endDate, settings, categories }))
      .filter((r) => r.sessions.length > 0 || r.grossCents > 0);
  }, [sessions, instructors, filterInstructor, startDate, endDate, settings, categories]);

  const periodSessions = useMemo(
    () => (sessions || []).filter((s) => s.date >= startDate && s.date <= endDate),
    [sessions, startDate, endDate]
  );
  const unassigned = periodSessions.filter((s) => s.attended.length > 0 && (s.instructorIds || []).length === 0);
  const missingRates = categories.filter((c) => {
    if (isPrivateCourse(c)) return PRIVATE_RATE_KEYS.some((k) => (settings.privateRates[c.id] || {})[k.key] == null) && settings.rates[c.id] == null;
    return settings.rates[c.id] == null;
  });

  // ---------- Tarifas ----------
  const openRates = () => {
    setRatesForm({
      rates: Object.fromEntries(categories.map((c) => [c.id, dollars(settings.rates[c.id])])),
      privateRates: Object.fromEntries(categories.filter(isPrivateCourse).map((c) => [
        c.id,
        Object.fromEntries(PRIVATE_RATE_KEYS.map((k) => [k.key, dollars((settings.privateRates[c.id] || {})[k.key])])),
      ])),
      exempt: dollars(settings.exemptCents),
      retentionPct: String(settings.retentionPct),
    });
    setRatesMsg('');
    setRatesOpen(true);
  };

  const saveRates = async () => {
    if (!ratesForm) return;
    setRatesSaving(true);
    try {
      const rates = {};
      Object.entries(ratesForm.rates).forEach(([id, v]) => { if (String(v).trim() !== '') rates[id] = toCents(v); });
      const privateRates = {};
      Object.entries(ratesForm.privateRates).forEach(([id, byKey]) => {
        const clean = {};
        Object.entries(byKey).forEach(([k, v]) => { if (String(v).trim() !== '') clean[k] = toCents(v); });
        if (Object.keys(clean).length) privateRates[id] = clean;
      });
      const next = {
        rates,
        privateRates,
        exemptCents: toCents(ratesForm.exempt),
        retentionPct: Math.max(0, Math.min(100, parseFloat(ratesForm.retentionPct) || 0)),
        updatedAt: new Date(),
      };
      await setDoc(doc(db, 'settings', INSTRUCTOR_PAY_SETTINGS_DOC), next);
      setSettings(normalizeSettings(next));
      setRatesMsg('Tarifas guardadas.');
      setRatesOpen(false);
    } catch (e) {
      console.error(e);
      setRatesMsg('No se pudieron guardar las tarifas.');
    }
    setRatesSaving(false);
  };

  // ---------- Asignar instructores a una sesión ----------
  const openAssign = (s) => {
    const checked = {};
    (s.instructorIds || []).forEach((id) => { checked[id] = true; });
    setAssignChecked(checked);
    setAssignSession(s);
  };

  const saveAssign = async () => {
    if (!assignSession) return;
    setAssignSaving(true);
    const ids = instructors.filter((i) => assignChecked[i.id]).map((i) => i.id);
    const names = instructors.filter((i) => assignChecked[i.id]).map((i) => i.name);
    try {
      // Mismo doc que graba Cursos al imprimir la hoja de asistencia
      await setDoc(doc(db, 'course_session_meta', assignSession.key), {
        categoryId: assignSession.categoryId,
        categoryName: assignSession.categoryName,
        date: assignSession.date,
        startTime: assignSession.startTime,
        instructorIds: ids,
        instructorNames: names,
        updatedAt: new Date(),
      }, { merge: true });
      setSessions((prev) => prev.map((s) => (s.key === assignSession.key ? { ...s, instructorIds: ids, instructorNames: names } : s)));
      setAssignSession(null);
    } catch (e) {
      console.error(e);
      alert('No se pudo guardar la asignación.');
    }
    setAssignSaving(false);
  };

  const printAll = () => { if (reports && reports.length) openInstructorReports(reports, settings); };
  const printOne = (r) => openInstructorReports([r], settings);
  const [pdfBusy, setPdfBusy] = useState(false);
  const pdfAll = async () => { if (!reports || !reports.length) return; setPdfBusy(true); try { await downloadInstructorReportsPdf(reports, settings); } catch (e) { console.error(e); alert('No se pudo generar el PDF.'); } setPdfBusy(false); };
  const pdfOne = async (r) => { setPdfBusy(true); try { await downloadInstructorReportsPdf([r], settings); } catch (e) { console.error(e); alert('No se pudo generar el PDF.'); } setPdfBusy(false); };

  return (
    <>
      <Helmet>
        <title> Pago a instructores | BBTC </title>
      </Helmet>

      <Container maxWidth="xl">
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography variant="h4">Pago a instructores</Typography>
          {canEdit && (
            <Button variant="outlined" onClick={openRates} startIcon={<Iconify icon="eva:settings-2-outline" />} disabled={loadingBase}>
              Tarifas y retención
            </Button>
          )}
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Reporte de pago de servicios profesionales: alumnos que asistieron a cada curso por instructor, tarifa por
          alumno, retención del {settings.retentionPct}% y neto a pagar. Los primeros {money(settings.exemptCents)} que cobra
          cada instructor en el año no llevan retención. El instructor de cada sesión sale de la hoja de asistencia
          impresa en Cursos (o se asigna aquí).
        </Typography>

        {ratesMsg && <Alert severity={ratesMsg.startsWith('No') ? 'error' : 'success'} sx={{ mb: 2 }} onClose={() => setRatesMsg('')}>{ratesMsg}</Alert>}
        {!loadingBase && instructors.length === 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>No hay empleados con rol <b>Instructor</b>. Asígnalo en Empleados.</Alert>
        )}
        {!loadingBase && missingRates.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Sin tarifa por alumno: {missingRates.map((c) => c.name).join(', ')}. {canEdit ? 'Configúrala en «Tarifas y retención»; mientras tanto esos alumnos cuentan $0.' : 'Esos alumnos cuentan $0 hasta que un admin la configure.'}
          </Alert>
        )}

        <Card sx={{ p: 3, mb: 3 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
            <TextField label="Desde" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} size="small" InputLabelProps={{ shrink: true }} />
            <TextField label="Hasta" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} size="small" InputLabelProps={{ shrink: true }} />
            <TextField select label="Instructor" value={filterInstructor} onChange={(e) => setFilterInstructor(e.target.value)} size="small" sx={{ minWidth: 220 }}>
              <MenuItem value="">Todos</MenuItem>
              {instructors.map((i) => <MenuItem key={i.id} value={i.id}>{i.name}</MenuItem>)}
            </TextField>
            <Button
              variant="contained"
              onClick={handleGenerate}
              disabled={loading || loadingBase}
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <Iconify icon="eva:search-fill" />}
            >
              Generar
            </Button>
            <Button variant="outlined" onClick={printAll} disabled={!reports || reports.length === 0} startIcon={<Iconify icon="eva:printer-outline" />}>
              Imprimir todos
            </Button>
            <Button variant="contained" color="inherit" onClick={pdfAll} disabled={pdfBusy || !reports || reports.length === 0} startIcon={pdfBusy ? <CircularProgress size={16} color="inherit" /> : <Iconify icon="eva:download-outline" />}>
              Descargar PDF
            </Button>
          </Stack>
          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        </Card>

        {reports && unassigned.length > 0 && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            {unassigned.length} sesión(es) con alumnos en el periodo no tienen instructor asignado y no se le pagan a nadie.
            Asígnalo en la lista de sesiones de abajo.
          </Alert>
        )}

        {reports && reports.length === 0 && (
          <Card sx={{ p: 3, mb: 3 }}>
            <Typography color="text.secondary">Ningún instructor tiene sesiones con alumnos entre {periodLabel(startDate, endDate)}.</Typography>
          </Card>
        )}

        {reports && reports.map((r) => (
          <Card key={r.instructor.id} sx={{ p: 3, mb: 3 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={1} sx={{ mb: 1 }}>
              <Box>
                <Typography variant="h6">{r.instructor.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  Periodo: {periodLabel(startDate, endDate)} · {r.sessions.length} sesión(es) · {r.students} alumno(s)
                </Typography>
              </Box>
              <Stack direction="row" spacing={1}>
                <Button size="small" variant="outlined" onClick={() => printOne(r)} startIcon={<Iconify icon="eva:printer-outline" />}>
                  Imprimir
                </Button>
                <Button size="small" variant="contained" color="inherit" onClick={() => pdfOne(r)} disabled={pdfBusy} startIcon={<Iconify icon="eva:download-outline" />}>
                  PDF
                </Button>
              </Stack>
            </Stack>
            {r.missingRate > 0 && (
              <Alert severity="warning" sx={{ mb: 1 }}>{r.missingRate} alumno(s) de cursos sin tarifa configurada cuentan $0.</Alert>
            )}
            <Grid container spacing={2}>
              <Grid item xs={12} md={7}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Desglose de cursos impartidos</Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Descripción / Tipo de curso</TableCell>
                      <TableCell align="right">Cantidad</TableCell>
                      <TableCell align="right">Tarifa</TableCell>
                      <TableCell align="right">Subtotal</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {r.lines.length === 0 && <TableRow><TableCell colSpan={4}><Typography color="text.secondary" variant="body2">Sin alumnos en el periodo.</Typography></TableCell></TableRow>}
                    {r.lines.map((l) => (
                      <TableRow key={`${l.label}-${l.rateCents}`}>
                        <TableCell>{l.label}</TableCell>
                        <TableCell align="right">{l.count}</TableCell>
                        <TableCell align="right">{money(l.rateCents)}</TableCell>
                        <TableCell align="right">{money(l.subtotalCents)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Grid>
              <Grid item xs={12} md={5}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Resumen financiero</Typography>
                <Card variant="outlined" sx={{ p: 2 }}>
                  <Stack direction="row" justifyContent="space-between"><Typography>Total bruto</Typography><Typography>{money(r.grossCents)}</Typography></Stack>
                  <Stack direction="row" justifyContent="space-between"><Typography variant="body2" color="text.secondary">Acumulado del año antes del periodo</Typography><Typography variant="body2" color="text.secondary">{money(r.ytdBeforeCents)}</Typography></Stack>
                  {r.exemptUsedCents > 0 && (
                    <>
                      <Stack direction="row" justifyContent="space-between"><Typography>Exento (primeros {money(settings.exemptCents)} del año)</Typography><Typography>− {money(r.exemptUsedCents)}</Typography></Stack>
                      <Stack direction="row" justifyContent="space-between"><Typography>Base sujeta a retención</Typography><Typography>{money(r.taxableCents)}</Typography></Stack>
                    </>
                  )}
                  <Stack direction="row" justifyContent="space-between"><Typography>Retención servicios profesionales (−{settings.retentionPct}%)</Typography><Typography>− {money(r.retentionCents)}</Typography></Stack>
                  <Divider sx={{ my: 1 }} />
                  <Stack direction="row" justifyContent="space-between"><Typography variant="h6">Total neto a pagar</Typography><Typography variant="h6">{money(r.netCents)}</Typography></Stack>
                  <Typography variant="caption" color="text.secondary">Acumulado del año después de este pago: {money(r.ytdAfterCents)}</Typography>
                </Card>
              </Grid>
            </Grid>
          </Card>
        ))}

        {reports && (
          <Card sx={{ mb: 3 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ p: 2 }}>
              <Typography variant="subtitle1">Sesiones de curso en el periodo ({periodSessions.length})</Typography>
              <Typography variant="caption" color="text.secondary">Solo cuentan los alumnos marcados «Asistió» en Cursos.</Typography>
            </Stack>
            <TableContainer sx={{ maxHeight: 520 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Fecha</TableCell>
                    <TableCell>Hora</TableCell>
                    <TableCell>Curso</TableCell>
                    <TableCell align="right">Asistieron</TableCell>
                    <TableCell align="right">Inscritos</TableCell>
                    <TableCell>Instructor(es)</TableCell>
                    <TableCell align="right" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {periodSessions.length === 0 && (
                    <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>No hay sesiones de curso en ese rango.</TableCell></TableRow>
                  )}
                  {periodSessions.map((s) => {
                    const noInstructor = (s.instructorIds || []).length === 0;
                    return (
                      <TableRow key={s.key} hover>
                        <TableCell>{s.date}</TableCell>
                        <TableCell>{formatTime12(s.startTime)}{s.endTime ? ` - ${formatTime12(s.endTime)}` : ''}</TableCell>
                        <TableCell>{s.categoryName}</TableCell>
                        <TableCell align="right">{s.attended.length}</TableCell>
                        <TableCell align="right">{s.total}</TableCell>
                        <TableCell>
                          {noInstructor
                            ? <Chip size="small" label={s.attended.length > 0 ? 'Sin instructor' : '—'} color={s.attended.length > 0 ? 'warning' : 'default'} variant="outlined" />
                            : (s.instructorNames || []).map((n) => <Chip key={n} size="small" label={n} sx={{ mr: 0.5 }} />)}
                          {(s.instructorIds || []).length > 1 && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Cada instructor cobra la tarifa completa</Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {canEdit && <Button size="small" onClick={() => openAssign(s)}>{noInstructor ? 'Asignar' : 'Cambiar'}</Button>}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        )}
      </Container>

      {/* Tarifas y retención */}
      <Dialog open={ratesOpen} onClose={() => !ratesSaving && setRatesOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Tarifas por alumno y retención</DialogTitle>
        <DialogContent>
          {ratesForm && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Lo que se le paga al instructor por cada alumno que asistió, según el curso.
              </Typography>
              {categories.filter((c) => !isPrivateCourse(c)).map((c) => (
                <TextField
                  key={c.id}
                  size="small"
                  label={`${c.name} ($ por alumno)`}
                  value={ratesForm.rates[c.id] || ''}
                  onChange={(e) => setRatesForm({ ...ratesForm, rates: { ...ratesForm.rates, [c.id]: e.target.value } })}
                  placeholder="Ej: 20.00"
                  inputProps={{ inputMode: 'decimal' }}
                />
              ))}
              {categories.filter(isPrivateCourse).map((c) => (
                <Box key={c.id}>
                  <Divider sx={{ mb: 1 }}>{c.name}</Divider>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    El curso privado se paga según idioma y si es licencia nueva o renovación.
                  </Typography>
                  <Grid container spacing={1}>
                    {PRIVATE_RATE_KEYS.map((k) => (
                      <Grid item xs={12} sm={6} key={k.key}>
                        <TextField
                          size="small"
                          fullWidth
                          label={`${k.label} ($)`}
                          value={(ratesForm.privateRates[c.id] || {})[k.key] || ''}
                          onChange={(e) => setRatesForm({
                            ...ratesForm,
                            privateRates: { ...ratesForm.privateRates, [c.id]: { ...(ratesForm.privateRates[c.id] || {}), [k.key]: e.target.value } },
                          })}
                          inputProps={{ inputMode: 'decimal' }}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              ))}
              <Divider>Retención por servicios profesionales</Divider>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  size="small"
                  fullWidth
                  label="Monto exento por año ($)"
                  value={ratesForm.exempt}
                  onChange={(e) => setRatesForm({ ...ratesForm, exempt: e.target.value })}
                  helperText="Los primeros $ del año natural sin retención (por instructor)"
                  inputProps={{ inputMode: 'decimal' }}
                />
                <TextField
                  size="small"
                  fullWidth
                  label="Retención (%)"
                  value={ratesForm.retentionPct}
                  onChange={(e) => setRatesForm({ ...ratesForm, retentionPct: e.target.value.replace(/[^0-9.]/g, '') })}
                  helperText="Sobre lo que pase del monto exento"
                  inputProps={{ inputMode: 'decimal' }}
                />
              </Stack>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRatesOpen(false)} disabled={ratesSaving}>Cancelar</Button>
          <Button variant="contained" onClick={saveRates} disabled={ratesSaving}>{ratesSaving ? 'Guardando...' : 'Guardar'}</Button>
        </DialogActions>
      </Dialog>

      {/* Asignar instructores a una sesión */}
      <Dialog open={Boolean(assignSession)} onClose={() => !assignSaving && setAssignSession(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Instructor(es) de la sesión</DialogTitle>
        <DialogContent>
          {assignSession && (
            <>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {assignSession.categoryName} · {assignSession.date} {formatTime12(assignSession.startTime)} · {assignSession.attended.length} alumno(s)
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Es lo mismo que marcar el instructor al imprimir la hoja de asistencia en Cursos.
              </Typography>
              <Collapse in>
                {instructors.map((i) => (
                  <FormControlLabel
                    key={i.id}
                    sx={{ display: 'flex' }}
                    control={<Checkbox checked={Boolean(assignChecked[i.id])} onChange={(e) => setAssignChecked({ ...assignChecked, [i.id]: e.target.checked })} />}
                    label={i.name}
                  />
                ))}
              </Collapse>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignSession(null)} disabled={assignSaving}>Cancelar</Button>
          <Button variant="contained" onClick={saveAssign} disabled={assignSaving}>{assignSaving ? 'Guardando...' : 'Guardar'}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
